// Distância (em ms e em pixels de tela) abaixo da qual o segundo clique de um
// duplo clique é tratado como parte do gesto de fechar, e não como novo vértice.
const DBLCLICK_WINDOW_MS = 350
const DBLCLICK_TOLERANCE_PX = 8
const MIN_CIRCLE_RADIUS_M = 10

export class FenceDrawingHandler {
  constructor(mapController, map) {
    this.mapController = mapController
    this.map = map
    this.drawingMode = null
    this.isDrawingModeEnabled = false

    this.currentOverlay = null
    this.existingOverlays = []

    // Polygon drawing state
    this._polygonVertices = []
    this._vertexMarkers = []
    this._previewPolyline = null
    this._previewPolygon = null
    this._cursorLatLng = null
    this._lastVertexAt = 0

    // Circle drawing state
    this._circleCenter = null
    this._previewCircle = null
    this._previewRadius = 0

    // Google Maps API listeners (active while drawing)
    this._clickListener = null
    this._dblClickListener = null
    this._mouseMoveListener = null
    this._keyDownHandler = null
  }

  async init() {
    await google.maps.importLibrary('geometry')
  }

  // --- Public API ---

  setDrawingMode(mode) {
    this.drawingMode = mode
    this.isDrawingModeEnabled = true
    this._startDrawing()
  }

  stopDrawing() {
    this.isDrawingModeEnabled = false
    this.drawingMode = null
    this._clearPreview()
    this._removeDrawingListeners()
    this._notifyState()
  }

  // Descarta o traçado em andamento e volta o mapa ao estado anterior ao gesto.
  cancelDrawing() {
    if (!this.isDrawingModeEnabled) return
    this._clearPreview()
    this._notifyState()
  }

  // Remove o último vértice do polígono em andamento.
  undoLastVertex() {
    if (this.drawingMode !== 'polygon' || this._polygonVertices.length === 0) return

    this._polygonVertices.pop()
    this._removeLastVertexMarker()
    this._updatePolygonPreview()
    this._notifyState()
  }

  // Fecha o polígono em andamento (equivalente ao duplo clique).
  finishShape() {
    if (this.drawingMode === 'polygon') this._completePolygon()
  }

  // Descarta o desenho concluído e reabre o modo de desenho do mesmo tipo.
  restartDrawing() {
    const mode = this.drawingMode || this.mapController.fenceTypeValue
    this.clearAll()
    if (mode && mode !== 'prompt') this.setDrawingMode(mode)
  }

  clearAll() {
    this.stopDrawing()
    this.clearExistingOverlays()

    const inputs = [
      document.querySelector('input[name="electronic_fence[area]"]'),
      document.getElementById('fence_area'),
      document.getElementById('fence_shape')
    ]
    inputs.forEach(input => { if (input) input.value = '' })
  }

  updateColor(color) {
    const options = { fillColor: color, strokeColor: color }

    if (this.currentOverlay) {
      this.currentOverlay.setOptions(options)
    }

    if (this._previewPolyline) this._previewPolyline.setOptions({ strokeColor: color })
    if (this._previewPolygon) this._previewPolygon.setOptions(options)
    if (this._previewCircle) this._previewCircle.setOptions(options)
    this._vertexMarkers.forEach(marker => marker.setOptions({ icon: this._vertexIcon(color) }))
  }

  loadExistingCoords() {
    const areaInput = document.querySelector('input[name="electronic_fence[area]"]') ||
      document.getElementById('fence_area') ||
      document.getElementById('fence_shape')

    let data = null

    if (!areaInput?.value) {
      const fenceDataScript = document.getElementById('fence-data')
      if (fenceDataScript) {
        try { data = JSON.parse(fenceDataScript.textContent).coordinates } catch { return }
      } else {
        return
      }
    } else {
      try { data = JSON.parse(areaInput.value) } catch { return }
    }

    if (!data) return

    try {
      if (data.type === 'circle') {
        this._loadCircle(data)
      } else if (data.type === 'polygon' || Array.isArray(data)) {
        this._loadPolygon(data)
      }
    } catch (error) {
      console.error('FenceDrawingHandler: error loading coords', error)
    }
  }

  destroy() {
    this.stopDrawing()
    this.clearExistingOverlays()
  }

  // Estado consumido pela barra de controles do modal.
  get state() {
    return {
      mode: this.drawingMode,
      drawing: this.isDrawingModeEnabled,
      vertexCount: this._polygonVertices.length,
      hasCenter: Boolean(this._circleCenter),
      radius: this._previewRadius,
      canUndo: this.drawingMode === 'polygon' && this._polygonVertices.length > 0,
      canFinish: this.drawingMode === 'polygon' && this._polygonVertices.length >= 3,
      hasShape: Boolean(this.currentOverlay)
    }
  }

  // --- Polygon drawing ---

  _setupPolygonDrawing() {
    this._polygonVertices = []

    this._clickListener = this.map.addListener('click', (e) => {
      if (!this.isDrawingModeEnabled) return
      // Vértice entra imediatamente: o segundo clique do duplo clique é
      // descartado depois, em _completePolygon, comparando tempo e posição.
      this._addPolygonVertex(e.latLng)
    })

    this._dblClickListener = this.map.addListener('dblclick', (e) => {
      e.stop() // Prevent map zoom
      this._completePolygon(e.latLng)
    })

    this._mouseMoveListener = this.map.addListener('mousemove', (e) => {
      if (!this.isDrawingModeEnabled || this._polygonVertices.length === 0) return
      this._cursorLatLng = e.latLng
      this._updatePolygonPreview()
    })
  }

  _addPolygonVertex(latLng) {
    this._polygonVertices.push(latLng)
    this._lastVertexAt = Date.now()
    this._addVertexMarker(latLng)
    this._updatePolygonPreview()
    this._notifyState()
  }

  // O caminho do preview inclui o cursor e volta ao primeiro vértice, para o
  // usuário ver a aresta em formação e o anel que ela vai fechar.
  _previewPath() {
    const path = [...this._polygonVertices]
    if (this._cursorLatLng && this.isDrawingModeEnabled) path.push(this._cursorLatLng)
    if (path.length >= 3) path.push(this._polygonVertices[0])
    return path
  }

  _updatePolygonPreview() {
    const color = this.getCurrentColor()
    const path = this._previewPath()

    if (this._previewPolyline) {
      this._previewPolyline.setPath(path)
    } else {
      this._previewPolyline = new google.maps.Polyline({
        path,
        strokeColor: color,
        strokeOpacity: 0.9,
        strokeWeight: 2,
        clickable: false,
        map: this.map
      })
    }

    if (path.length >= 3) {
      if (this._previewPolygon) {
        this._previewPolygon.setPath(path)
      } else {
        this._previewPolygon = new google.maps.Polygon({
          paths: path,
          fillColor: color,
          fillOpacity: 0.25,
          strokeOpacity: 0,
          clickable: false,
          map: this.map
        })
      }
    } else if (this._previewPolygon) {
      this._previewPolygon.setMap(null)
      this._previewPolygon = null
    }
  }

  _vertexIcon(color) {
    return {
      path: google.maps.SymbolPath.CIRCLE,
      scale: 5,
      fillColor: '#ffffff',
      fillOpacity: 1,
      strokeColor: color || this.getCurrentColor(),
      strokeWeight: 2
    }
  }

  _addVertexMarker(latLng) {
    this._vertexMarkers.push(new google.maps.Marker({
      position: latLng,
      icon: this._vertexIcon(),
      clickable: false,
      map: this.map
    }))
  }

  _removeLastVertexMarker() {
    const marker = this._vertexMarkers.pop()
    if (marker) marker.setMap(null)
  }

  _clearVertexMarkers() {
    this._vertexMarkers.forEach(marker => marker.setMap(null))
    this._vertexMarkers = []
  }

  // `at` é a posição do duplo clique, quando veio de lá: o clique que a
  // originou já entrou como vértice e precisa ser descartado.
  _completePolygon(at = null) {
    if (at) this._dropVertexFromDoubleClick(at)

    if (this._polygonVertices.length < 3) {
      this._notifyState()
      return
    }

    const vertices = [...this._polygonVertices]
    this._clearPreview()
    this._removeDrawingListeners()

    const color = this.getCurrentColor()
    const polygon = new google.maps.Polygon({
      paths: vertices,
      fillColor: color,
      fillOpacity: 0.3,
      strokeWeight: 2,
      strokeColor: color,
      clickable: true,
      editable: true,
      draggable: true,
      map: this.map
    })

    this.clearExistingOverlays()
    this.currentOverlay = polygon
    this.existingOverlays.push(polygon)

    this._saveOverlay({ type: 'polygon', overlay: polygon })
    this._setupEditListeners(polygon, 'polygon')
    this._finishDrawing()
  }

  _dropVertexFromDoubleClick(latLng) {
    const last = this._polygonVertices[this._polygonVertices.length - 1]
    if (!last) return
    if (Date.now() - this._lastVertexAt > DBLCLICK_WINDOW_MS) return
    if (this._pixelDistance(last, latLng) > DBLCLICK_TOLERANCE_PX) return

    this._polygonVertices.pop()
    this._removeLastVertexMarker()
  }

  // Compara em pixels de tela, não em metros: a tolerância do gesto é do
  // ponteiro, e independe do zoom.
  _pixelDistance(a, b) {
    const projection = this.map.getProjection()
    if (!projection) return Number.POSITIVE_INFINITY

    const scale = 2 ** this.map.getZoom()
    const pa = projection.fromLatLngToPoint(a)
    const pb = projection.fromLatLngToPoint(b)
    return Math.hypot(pa.x - pb.x, pa.y - pb.y) * scale
  }

  // --- Circle drawing ---

  _setupCircleDrawing() {
    this._circleCenter = null
    this._previewRadius = 0

    this._clickListener = this.map.addListener('click', (e) => {
      if (!this.isDrawingModeEnabled) return

      if (!this._circleCenter) {
        this._circleCenter = e.latLng
        this._startCirclePreview(e.latLng)
        this._notifyState()
      } else {
        const radius = google.maps.geometry.spherical.computeDistanceBetween(
          this._circleCenter, e.latLng
        )
        this._completeCircle(this._circleCenter, radius)
      }
    })

    this._mouseMoveListener = this.map.addListener('mousemove', (e) => {
      if (!this._circleCenter || !this._previewCircle) return
      const radius = google.maps.geometry.spherical.computeDistanceBetween(
        this._circleCenter, e.latLng
      )
      this._previewRadius = radius
      this._previewCircle.setRadius(radius)
      this._notifyState()
    })
  }

  _startCirclePreview(center) {
    const color = this.getCurrentColor()
    // Raio 0: sem o piscar de um círculo arbitrário antes do primeiro mousemove.
    this._previewCircle = new google.maps.Circle({
      center,
      radius: 0,
      fillColor: color,
      fillOpacity: 0.25,
      strokeColor: color,
      strokeOpacity: 0.8,
      strokeWeight: 2,
      clickable: false,
      map: this.map
    })
  }

  _completeCircle(center, radius) {
    if (radius < MIN_CIRCLE_RADIUS_M) {
      this._notifyState()
      return
    }

    const centerLatLng = center
    this._clearPreview()
    this._removeDrawingListeners()

    const color = this.getCurrentColor()
    const circle = new google.maps.Circle({
      center: { lat: centerLatLng.lat(), lng: centerLatLng.lng() },
      radius,
      fillColor: color,
      fillOpacity: 0.3,
      strokeWeight: 2,
      strokeColor: color,
      clickable: true,
      editable: true,
      draggable: true,
      map: this.map
    })

    this.clearExistingOverlays()
    this.currentOverlay = circle
    this.existingOverlays.push(circle)

    this._saveOverlay({ type: 'circle', overlay: circle })
    this._setupEditListeners(circle, 'circle')
    this._finishDrawing()
  }

  // --- Shared drawing helpers ---

  _startDrawing() {
    this._clearPreview()
    this._removeDrawingListeners()

    if (this.drawingMode === 'polygon') {
      this._setupPolygonDrawing()
    } else if (this.drawingMode === 'circle') {
      this._setupCircleDrawing()
    }

    this._addKeyboardListeners()
    this._notifyState()
  }

  _addKeyboardListeners() {
    this._keyDownHandler = (event) => {
      if (!this.isDrawingModeEnabled) return

      if (event.key === 'Escape') {
        event.preventDefault()
        this.cancelDrawing()
      } else if (event.key === 'Backspace' || (event.key.toLowerCase() === 'z' && (event.ctrlKey || event.metaKey))) {
        event.preventDefault()
        this.undoLastVertex()
      } else if (event.key === 'Enter') {
        event.preventDefault()
        this.finishShape()
      }
    }
    document.addEventListener('keydown', this._keyDownHandler)
  }

  _clearPreview() {
    if (this._previewPolyline) { this._previewPolyline.setMap(null); this._previewPolyline = null }
    if (this._previewPolygon) { this._previewPolygon.setMap(null); this._previewPolygon = null }
    if (this._previewCircle) { this._previewCircle.setMap(null); this._previewCircle = null }
    this._clearVertexMarkers()
    this._polygonVertices = []
    this._circleCenter = null
    this._cursorLatLng = null
    this._previewRadius = 0
  }

  _removeDrawingListeners() {
    if (this._clickListener) { google.maps.event.removeListener(this._clickListener); this._clickListener = null }
    if (this._dblClickListener) { google.maps.event.removeListener(this._dblClickListener); this._dblClickListener = null }
    if (this._mouseMoveListener) { google.maps.event.removeListener(this._mouseMoveListener); this._mouseMoveListener = null }
    if (this._keyDownHandler) { document.removeEventListener('keydown', this._keyDownHandler); this._keyDownHandler = null }
  }

  _finishDrawing() {
    this.isDrawingModeEnabled = false
    this.drawingMode = null
    this._removeDrawingListeners()
    this._notifyState()
  }

  _notifyState() {
    this.mapController.onDrawingStateChange?.(this.state)
  }

  _saveOverlay({ type, overlay }) {
    let data
    if (type === 'polygon') {
      data = this._getPolygonData(overlay)
    } else if (type === 'circle') {
      data = this._getCircleData(overlay)
    }
    if (data) this.updateInputs(data)
  }

  // --- Edit listeners (post-draw) ---

  _setupEditListeners(overlay, type) {
    if (type === 'polygon') {
      overlay.getPath().addListener('set_at', () => this._updatePolygonData(overlay))
      overlay.getPath().addListener('insert_at', () => this._updatePolygonData(overlay))
      overlay.getPath().addListener('remove_at', () => this._updatePolygonData(overlay))
      overlay.addListener('dragend', () => this._updatePolygonData(overlay))
    } else if (type === 'circle') {
      overlay.addListener('center_changed', () => this._updateCircleData(overlay))
      overlay.addListener('radius_changed', () => this._updateCircleData(overlay))
      overlay.addListener('dragend', () => this._updateCircleData(overlay))
    }
  }

  // Alias for backward compat with loadCircle/loadPolygon paths
  setupLoadedOverlayListeners(overlay, type) {
    this._setupEditListeners(overlay, type)
  }

  // --- Load existing overlays ---

  _loadCircle(data) {
    const isShowMode = this.mapController.modeValue === 'show'
    const circle = new google.maps.Circle({
      center: data.center,
      radius: data.radius,
      fillColor: this.getCurrentColor(),
      fillOpacity: 0.3,
      strokeWeight: 2,
      strokeColor: this.getCurrentColor(),
      editable: !isShowMode,
      draggable: !isShowMode,
      clickable: !isShowMode,
      map: this.map
    })

    this.clearExistingOverlays()
    this.currentOverlay = circle
    this.existingOverlays.push(circle)

    if (!isShowMode) this._setupEditListeners(circle, 'circle')
    this.map.fitBounds(circle.getBounds())
    this._notifyState()
  }

  _loadPolygon(data) {
    const coordinates = Array.isArray(data) ? data : data.coordinates
    const isShowMode = this.mapController.modeValue === 'show'

    const polygon = new google.maps.Polygon({
      paths: coordinates,
      fillColor: this.getCurrentColor(),
      fillOpacity: 0.3,
      strokeWeight: 2,
      strokeColor: this.getCurrentColor(),
      editable: !isShowMode,
      draggable: !isShowMode,
      clickable: !isShowMode,
      map: this.map
    })

    this.clearExistingOverlays()
    this.currentOverlay = polygon
    this.existingOverlays.push(polygon)

    if (!isShowMode) this._setupEditListeners(polygon, 'polygon')

    const bounds = new google.maps.LatLngBounds()
    coordinates.forEach(coord => bounds.extend(coord))
    this.map.fitBounds(bounds)
    this._notifyState()
  }

  // --- Data helpers ---

  _getPolygonData(polygon) {
    const coordinates = []
    const path = polygon.getPath()
    for (let i = 0; i < path.getLength(); i++) {
      const point = path.getAt(i)
      coordinates.push({ lat: point.lat(), lng: point.lng() })
    }
    return { type: 'polygon', coordinates }
  }

  _getCircleData(circle) {
    return {
      type: 'circle',
      center: { lat: circle.getCenter().lat(), lng: circle.getCenter().lng() },
      radius: circle.getRadius()
    }
  }

  _updatePolygonData(polygon) {
    this.updateInputs(this._getPolygonData(polygon))
  }

  _updateCircleData(circle) {
    this.updateInputs(this._getCircleData(circle))
  }

  updateInputs(data) {
    const jsonData = JSON.stringify(data)
    const targets = [
      document.querySelector('input[name="electronic_fence[area]"]'),
      document.getElementById('fence_shape'),
      document.getElementById('fence_area')
    ]
    targets.forEach(el => { if (el) el.value = jsonData })
    document.dispatchEvent(new CustomEvent('areaUpdated', { detail: data }))
  }

  clearExistingOverlays() {
    this.existingOverlays.forEach(overlay => overlay.setMap(null))
    this.existingOverlays = []
    this.currentOverlay = null
  }

  getCurrentColor() {
    const colorInput = document.querySelector('input[name="electronic_fence[color]"]')
    if (colorInput?.value) return colorInput.value

    const fenceDataScript = document.getElementById('fence-data')
    if (fenceDataScript) {
      try {
        const scriptData = JSON.parse(fenceDataScript.textContent)
        if (scriptData.color) return scriptData.color
      } catch { }
    }

    return '#1F2742'
  }
}
